/**
 * Mood service — the persistence seam under MoodContext.
 *
 * Two modes, chosen at runtime by `moodBackendEnabled()`:
 *
 *  • LOCAL (default, and demo mode): check-ins and reflections live in
 *    localStorage, keyed by email — exactly the original on-device behaviour.
 *    This is also the offline/dev path, so the web app keeps working with no
 *    backend deployed. Default until `REACT_APP_MOOD_BACKEND=true` is set.
 *
 *  • BACKEND: reads/writes go to AppSync (GraphQLMoodService). A separate
 *    `:cache:` copy in localStorage backs offline reads; it is NOT the legacy
 *    on-device store, so a user's pre-backend history is never overwritten and
 *    stays available to import — on purpose, not silently (requirement 6/7).
 *
 * Consent: the caller (context) only persists when the user's `saveHistory`
 * preference is on. Import is an explicit, user-initiated action and is
 * idempotent (the backend keys on the record's client id), so re-importing
 * never duplicates.
 *
 * Privacy: no mood content is logged, and nothing is ever placed in a URL.
 */

import { isDemoMode } from './mockData';
import { GraphQLMoodService } from './graphqlMood';
import type { MoodCheckInRecord, MoodReflection } from '../contexts/MoodContext';

const NS = 'creyente:moodmate';
// Legacy on-device store (pre-backend). In LOCAL mode this is the live store; in
// BACKEND mode it is read-only and serves as the import source.
const checkInsKey = (email: string) => `${NS}:checkins:v1:${email}`;
const reflKey = (email: string) => `${NS}:reflections:v1:${email}`;
// Offline cache for BACKEND mode (kept distinct so it can never clobber the above).
const cacheCheckInsKey = (email: string) => `${NS}:cache:checkins:v1:${email}`;
const cacheReflKey = (email: string) => `${NS}:cache:reflections:v1:${email}`;
// Remembers that a user already imported their local history, so we don't nag.
const importedKey = (email: string) => `${NS}:imported:v1:${email}`;

const MAX_LOCAL = 2000;

export interface ImportResult {
  imported: number;
  skipped: number;
  failed: number;
}

/** Whether the deployed backend should be used. Off unless explicitly enabled. */
export const moodBackendEnabled = (): boolean =>
  !isDemoMode() && process.env.REACT_APP_MOOD_BACKEND === 'true';

// ── localStorage helpers (all guarded) ───────────────────────────────────────
function readArray<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
function writeArray(key: string, value: unknown[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode / quota — degrade to in-memory */
  }
}

export const readLocalCheckIns = (email: string): MoodCheckInRecord[] => readArray<MoodCheckInRecord>(checkInsKey(email));
export const readLocalReflections = (email: string): MoodReflection[] => readArray<MoodReflection>(reflKey(email));

/** How many legacy on-device records exist that an import could move up. */
export const localHistoryCount = (email: string): number =>
  email ? readLocalCheckIns(email).length + readLocalReflections(email).length : 0;

export const hasImported = (email: string): boolean => {
  try {
    return localStorage.getItem(importedKey(email)) === 'true';
  } catch {
    return false;
  }
};
const markImported = (email: string): void => {
  try {
    localStorage.setItem(importedKey(email), 'true');
  } catch {
    /* ignore */
  }
};

// Mode-aware storage key + cache writers.
const liveCheckInsKey = (email: string) => (moodBackendEnabled() ? cacheCheckInsKey(email) : checkInsKey(email));
const liveReflKey = (email: string) => (moodBackendEnabled() ? cacheReflKey(email) : reflKey(email));

/** Mirror the current arrays to local storage (live store in LOCAL, cache in BACKEND). */
export const cacheCheckIns = (email: string, list: MoodCheckInRecord[]): void => {
  if (email) writeArray(liveCheckInsKey(email), list.slice(0, MAX_LOCAL));
};
export const cacheReflections = (email: string, list: MoodReflection[]): void => {
  if (email) writeArray(liveReflKey(email), list);
};

// ── Loads ────────────────────────────────────────────────────────────────────
/** Load check-ins. Backend when enabled (falling back to cache offline), else local. */
export const loadCheckIns = async (email: string): Promise<MoodCheckInRecord[]> => {
  if (!email) return [];
  if (!moodBackendEnabled()) return readLocalCheckIns(email);
  try {
    const rows = await GraphQLMoodService.listCheckIns();
    writeArray(cacheCheckInsKey(email), rows.slice(0, MAX_LOCAL)); // warm offline cache
    return rows;
  } catch (err) {
    console.warn('[moodService] check-in load failed; using offline cache');
    return readArray<MoodCheckInRecord>(cacheCheckInsKey(email));
  }
};

export const loadReflections = async (email: string): Promise<MoodReflection[]> => {
  if (!email) return [];
  if (!moodBackendEnabled()) return readLocalReflections(email);
  try {
    const rows = await GraphQLMoodService.listReflections();
    writeArray(cacheReflKey(email), rows);
    return rows;
  } catch (err) {
    console.warn('[moodService] reflection load failed; using offline cache');
    return readArray<MoodReflection>(cacheReflKey(email));
  }
};

// ── Writes (the context updates state optimistically and calls these) ─────────
/** Returns true when the record is saved (or a LOCAL no-op), false when a backend save fails. */
export const createCheckIn = async (email: string, record: MoodCheckInRecord): Promise<boolean> => {
  if (!email) return false;
  if (!moodBackendEnabled()) return true; // LOCAL mode persists via cacheCheckIns
  return !!(await GraphQLMoodService.createCheckIn(record));
};

export const updateCheckIn = async (
  email: string,
  id: string,
  patch: Partial<Omit<MoodCheckInRecord, 'id' | 'createdAt'>>
): Promise<void> => {
  if (!email || !moodBackendEnabled()) return;
  await GraphQLMoodService.updateCheckIn(id, patch);
};

export const deleteCheckIn = async (email: string, id: string): Promise<void> => {
  if (!email || !moodBackendEnabled()) return;
  await GraphQLMoodService.deleteCheckIn(id);
};

export const saveReflection = async (email: string, reflection: MoodReflection, isNew: boolean): Promise<void> => {
  if (!email || !moodBackendEnabled()) return;
  await GraphQLMoodService.saveReflection(reflection, isNew);
};

export const deleteReflection = async (email: string, id: string): Promise<void> => {
  if (!email || !moodBackendEnabled()) return;
  await GraphQLMoodService.deleteReflection(id);
};

/** Delete every check-in remotely (BACKEND mode). Local clear is the caller's job. */
export const clearCheckInsRemote = async (email: string, records: MoodCheckInRecord[]): Promise<void> => {
  if (!email || !moodBackendEnabled()) return;
  await Promise.all(records.map((r) => GraphQLMoodService.deleteCheckIn(r.id).catch(() => undefined)));
  try {
    localStorage.removeItem(cacheCheckInsKey(email));
  } catch {
    /* ignore */
  }
};

export const clearReflectionsRemote = async (email: string, records: MoodReflection[]): Promise<void> => {
  if (!email || !moodBackendEnabled()) return;
  await Promise.all(records.map((r) => GraphQLMoodService.deleteReflection(r.id).catch(() => undefined)));
  try {
    localStorage.removeItem(cacheReflKey(email));
  } catch {
    /* ignore */
  }
};

/**
 * Import the legacy on-device history into the backend — explicit and consented.
 *
 * Idempotent: each record keeps its original client id, which the backend uses as
 * its key with a conditional put, so a record already in the backend is skipped
 * rather than duplicated. Per-record failures are counted, not fatal.
 */
export const importLocalToBackend = async (email: string): Promise<ImportResult> => {
  const result: ImportResult = { imported: 0, skipped: 0, failed: 0 };
  if (!email || !moodBackendEnabled()) return result;

  const localCheckIns = readLocalCheckIns(email);
  const localReflections = readLocalReflections(email);

  // What's already in the backend, so re-import is a no-op (belt-and-braces on
  // top of the backend's conditional put).
  let existingCheckInIds = new Set<string>();
  let existingReflectionIds = new Set<string>();
  try {
    const [serverCheckIns, serverReflections] = await Promise.all([
      GraphQLMoodService.listCheckIns(),
      GraphQLMoodService.listReflections(),
    ]);
    existingCheckInIds = new Set(serverCheckIns.map((r) => r.id));
    existingReflectionIds = new Set(serverReflections.map((r) => r.id));
  } catch {
    /* if the pre-check fails we still try to import; the backend put is idempotent */
  }

  for (const record of localCheckIns) {
    if (existingCheckInIds.has(record.id)) {
      result.skipped += 1;
      continue;
    }
    try {
      const created = await GraphQLMoodService.createCheckIn(record);
      if (created) result.imported += 1;
      else result.failed += 1;
    } catch {
      result.failed += 1;
    }
  }

  for (const reflection of localReflections) {
    if (existingReflectionIds.has(reflection.id)) {
      result.skipped += 1;
      continue;
    }
    try {
      const saved = await GraphQLMoodService.saveReflection(reflection, true);
      if (saved) result.imported += 1;
      else result.failed += 1;
    } catch {
      result.failed += 1;
    }
  }

  if (result.failed === 0) markImported(email);
  return result;
};
